import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchServiceToken, validateMagicLink, ENV } from "./envConfig";
import { detectPlatform } from "./deepLinkUtils";
import { APP_OPEN_TIMEOUT_MS } from "./actionConfig";
import "./MagicLinkHandler.css";

// Witmeg Handle app — different from RewardStick Consumer
const WITMEG_HANDLE = {
  uriScheme: "witmeghandle",
  androidPackage: "com.ecologital.witmegHandle",
  iosStore: "https://apps.apple.com/au/app/witmeg-handle/id1591633495",
  androidStore: "https://play.google.com/store/apps/details?id=com.ecologital.witmegHandle&hl=en",
};

export default function MagicLinkHandler() {
  const { param1 } = useParams();

  const [status, setStatus] = useState("loading");
  const [resultMessage, setResultMessage] = useState("");
  const [launching, setLaunching] = useState(false);
  const [countdown, setCountdown] = useState(3);

  const platform = detectPlatform();
  const fullMagicUrl = `${ENV.SITE_BASE_URL}/mlink/${param1}`;

  // Step 1: Validate on mount
  useEffect(() => {
    if (!param1) {
      setStatus("error");
      setResultMessage("No magic link key found in URL.");
      return;
    }

    async function validate() {
      try {
        const token = await fetchServiceToken("orgregistry");
        const result = await validateMagicLink(param1, token, fullMagicUrl);
        setResultMessage(result.Result);
        if (result.Status === true) {
          setStatus("active");
        } else {
          setStatus("used");
        }
      } catch (err) {
        console.error("Magic link validation error:", err);
        setStatus("error");
        setResultMessage("Unable to validate the link. Please try again.");
      }
    }

    validate();
  }, [param1, fullMagicUrl]);

  // Step 2: Countdown then launch app
  useEffect(() => {
    if (status !== "active" || launching) return;

    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }

    setLaunching(true);
    launchApp();
  }, [status, countdown, launching]);

  // Launch Witmeg Handle app with full magic URL as param
  function launchApp() {
    const encodedUrl = encodeURIComponent(fullMagicUrl);

    if (platform === "android") {
      const intentUrl =
        `intent://open?url=${encodedUrl}#Intent;` +
        `scheme=${WITMEG_HANDLE.uriScheme};` +
        `package=${WITMEG_HANDLE.androidPackage};` +
        `S.browser_fallback_url=${encodeURIComponent(WITMEG_HANDLE.androidStore)};` +
        `end`;
      window.location.href = intentUrl;

    } else if (platform === "ios") {
      window.location.href = `${WITMEG_HANDLE.uriScheme}://open?url=${encodedUrl}`;

      const timer = setTimeout(() => {
        window.location.href = WITMEG_HANDLE.iosStore;
      }, APP_OPEN_TIMEOUT_MS);

      document.addEventListener("visibilitychange", () => {
        if (document.hidden) clearTimeout(timer);
      }, { once: true });

    }
    // Desktop: do nothing, just show active state with buttons
  }

  function handleOpenApp() {
    launchApp();
  }

  function handleDownloadApp() {
    if (platform === "ios") {
      window.open(WITMEG_HANDLE.iosStore, "_blank");
    } else {
      window.open(WITMEG_HANDLE.androidStore, "_blank");
    }
  }

  return (
    <div className="ml-root">
      <div className={`ml-card ${status === "used" || status === "error" ? "ml-card--error" : ""}`}>

        <Logo />

        {status === "loading" && (
          <>
            <div className="ml-spinner" />
            <p className="ml-title">Validating your link…</p>
            <p className="ml-subtitle">Please wait a moment.</p>
          </>
        )}

        {status === "active" && (
          <>
            <div className="ml-icon ml-icon--success">✓</div>
            <h2 className="ml-title">Link is Active</h2>
            <p className="ml-subtitle">{resultMessage}</p>

            {!launching ? (
              <>
                <p className="ml-status">
                  Opening Witmeg Handle in
                  <span className="ml-countdown"> {countdown}</span>…
                </p>
                <div className="ml-progress">
                  <div
                    className="ml-progress-bar"
                    style={{ animationDuration: `${countdown + 1}s` }}
                  />
                </div>
              </>
            ) : (
              <p className="ml-status ml-status--launched">
                {platform === "ios"
                  ? "App launching… tap below if nothing opens."
                  : "Redirecting to the app…"}
              </p>
            )}

            <div className="ml-btn-group">
              <button className="ml-btn ml-btn--primary" onClick={handleOpenApp}>
                Open in Witmeg Handle
              </button>
              <button className="ml-btn ml-btn--secondary" onClick={handleDownloadApp}>
                Download Witmeg Handle
              </button>
            </div>

            {platform === "desktop" && (
              <p className="ml-hint">
                Scan this link on your mobile device for the best experience.
              </p>
            )}
          </>
        )}

        {status === "used" && (
          <>
            <div className="ml-icon ml-icon--error">✕</div>
            <h2 className="ml-title">Link Already Used</h2>
            <p className="ml-subtitle">{resultMessage}</p>
            <p className="ml-hint">
              This magic link has already been used and is no longer valid.
              Please request a new link.
            </p>
            <button className="ml-btn ml-btn--secondary" onClick={handleDownloadApp}>
              Download Witmeg Handle
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="ml-icon ml-icon--error">!</div>
            <h2 className="ml-title">Something Went Wrong</h2>
            <p className="ml-subtitle">{resultMessage}</p>
            <p className="ml-hint">
              Please check your link and try again, or contact support.
            </p>
            <button className="ml-btn ml-btn--secondary" onClick={handleDownloadApp}>
              Download Witmeg Handle
            </button>
          </>
        )}

        {param1 && (
          <p className="ml-key">Key: {param1.slice(0, 8)}…</p>
        )}

      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="ml-logo">
      <span className="ml-logo-r">R</span>
      <span className="ml-logo-text">ewardStick</span>
    </div>
  );
}
